<div class="admin-import-module__run-result">
  <p class="bold">
    {{ 'TRANS__IMPORT__RUN_COMPLETE' | translate:
       {created: runResult.created, failed: runResult.failed} }}
  </p>

  @if (unresolvedFieldLabels.length) {
    <p class="admin-import-module__warn">
      {{ 'TRANS__IMPORT__RUN_UNRESOLVED_FIELDS' | translate }}:
      {{ unresolvedFieldLabels.join(', ') }}
    </p>
  }

  @if (runResult.created_revision_overridden) {
    <p class="admin-import-module__hint">
      {{ 'TRANS__IMPORT__RUN_CREATED_REVISION_OVERRIDDEN' | translate:
         {count: runResult.created_revision_overridden} }}
    </p>
  }

  @if (runResult.created_revision_unresolved?.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_CREATED_REVISION_UNRESOLVED' | translate:
           {count: createdRevisionUnresolvedTotal} }}
      </summary>
      <ul>
        @for (cr of runResult.created_revision_unresolved; track cr.kind + cr.value) {
          <li>
            <span class="bold">{{ createdRevisionKindLabel(cr.kind) }}</span>:
            {{ cr.value }} ({{ cr.count }})
          </li>
        }
      </ul>
    </details>
  }

  @if (runResult.unresolved_codes.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_UNRESOLVED_CODES' | translate:
           {count: runResult.unresolved_codes.length} }}
      </summary>
      <ul>
        @for (uc of runResult.unresolved_codes; track uc.column + uc.code) {
          <li>
            <span class="bold">{{ uc.column }}</span>:
            {{ uc.code }} ({{ uc.count }})
          </li>
        }
      </ul>
    </details>
  }

  @if (conceptsCreatedTotal(); as conceptsCreated) {
    <p class="admin-import-module__hint">
      {{ 'TRANS__IMPORT__RUN_CONCEPTS_CREATED' | translate: {count: conceptsCreated} }}
    </p>
  }

  @if (runResult.unresolved_concept_values.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_UNRESOLVED_CONCEPTS' | translate:
           {count: runResult.unresolved_concept_values.length} }}
      </summary>
      <ul>
        @for (uc of unresolvedConceptValues; track uc.entry.target_field + uc.entry.value) {
          <li>
            <span class="bold">{{ uc.label }}</span>:
            {{ uc.entry.value }} ({{ uc.entry.count }})
          </li>
        }
      </ul>
    </details>
  }

  @if (runResult.skipped_field_values?.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_SKIPPED_FIELDS' | translate:
           {count: skippedFieldValuesTotal()} }}
      </summary>
      <ul>
        @for (sv of runResult.skipped_field_values; track $index) {
          <li>
            <span class="bold">{{ sv.row }}</span> —
            @if (sv.media) {
              <span class="bold">{{ mediaValuesTitleKey | translate }} · {{ sv.field }}</span> ({{ sv.media }}):
            } @else {
              <span class="bold">{{ sv.field }}</span>:
            }
            {{ sv.value }}
            ({{ skippedValueReasons(sv.reasons) }})
          </li>
        }
        @if (droppedCount(skippedFieldValuesTotal(), runResult.skipped_field_values.length); as more) {
          <li class="admin-import-module__run-errors-more">
            {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
          </li>
        }
      </ul>
    </details>
  }

  @if (skippedGroups.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_SKIPPED_GROUPS' | translate:
           {count: skippedGroupsTotal()} }}
      </summary>
      <ul>
        @for (sg of skippedGroups; track sg.entry.row + sg.entry.card) {
          <li>
            <span class="bold">{{ sg.entry.row }}</span> —
            @if (sg.title) {
              <span class="bold">{{ sg.title }}</span>:
            }
            {{ sg.entry.missing.join(', ') }}
          </li>
        }
        @if (droppedCount(skippedGroupsTotal(), skippedGroups.length); as more) {
          <li class="admin-import-module__run-errors-more">
            {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
          </li>
        }
      </ul>
    </details>
  }

  @if (hasMedia()) {
    <p class="admin-import-module__run-media-summary">
      {{ 'TRANS__IMPORT__RUN_MEDIA_SUMMARY_FILES' | translate:
         {files: runResult.media_files_linked || 0, rows: runResult.media_linked || 0,
          alreadyLinked: mediaAlreadyLinkedTotal()} }}
    </p>

    @if (runResult.media_field_failures?.length) {
      <details class="admin-import-module__run-errors">
        <summary>
          {{ 'TRANS__IMPORT__RUN_MEDIA_FIELD_FAILURES' | translate:
             {count: mediaFieldFailuresTotal()} }}
        </summary>
        <ul>
          @for (err of runResult.media_field_failures ?? []; track err.row + err.media_id) {
            <li>
              <span class="bold">{{ err.row }}</span> ({{ err.file_name }}):
              {{ err.error }}
            </li>
          }
          @if (droppedCount(mediaFieldFailuresTotal(), runResult.media_field_failures?.length ?? 0); as more) {
            <li class="admin-import-module__run-errors-more">
              {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
            </li>
          }
        </ul>
      </details>
    }

    @if (runResult.media_files_without_row_total != null) {
      @let withoutRow = runResult.media_files_without_row ?? [];
      @if (withoutRow.length) {
        <details class="admin-import-module__run-errors admin-import-module__run-media-files-without-row">
          <summary>
            {{ 'TRANS__IMPORT__RUN_MEDIA_FILES_WITHOUT_ROW' | translate:
               {count: runResult.media_files_without_row_total} }}
          </summary>
          <ul>
            @for (file of withoutRow; track $index) {
              <li>{{ file.file_name }}@if (file.linked) {
                ({{ 'TRANS__IMPORT__PREFLIGHT_MEDIA_LINKED_MARK' | translate }})}</li>
            }
            @if (runResult.media_files_without_row_total > withoutRow.length) {
              <li class="admin-import-module__run-errors-more">
                {{ 'TRANS__IMPORT__RUN_MEDIA_FILES_WITHOUT_ROW_DOWNLOAD' | translate }}
              </li>
            }
          </ul>
        </details>
      } @else {
        <p class="admin-import-module__hint admin-import-module__run-media-files-without-row">
          {{ 'TRANS__IMPORT__RUN_MEDIA_FILES_WITHOUT_ROW' | translate:
             {count: runResult.media_files_without_row_total} }}
        </p>
      }
    }

    @if (runResult.media_unmatched?.length; as unmatchedShown) {
      <details class="admin-import-module__run-errors">
        <summary>
          {{ 'TRANS__IMPORT__RUN_MEDIA_UNMATCHED' | translate:
             {count: mediaUnmatchedTotal()} }}
        </summary>
        <ul>
          @for (m of runResult.media_unmatched ?? []; track m.row) {
            <li><span class="bold">{{ m.row }}</span>: {{ m.value }}</li>
          }
          @if (droppedCount(mediaUnmatchedTotal(), unmatchedShown); as more) {
            <li class="admin-import-module__run-errors-more">
              {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
            </li>
          }
        </ul>
      </details>
    }

    @if (runResult.media_ambiguous?.length; as ambiguousShown) {
      <details class="admin-import-module__run-errors">
        <summary>
          {{ 'TRANS__IMPORT__RUN_MEDIA_AMBIGUOUS' | translate:
             {count: mediaAmbiguousTotal()} }}
        </summary>
        <ul>
          @for (m of runResult.media_ambiguous ?? []; track m.row) {
            <li>
              <span class="bold">{{ m.row }}</span>:
              {{ m.value }} ({{ m.count }})
            </li>
          }
          @if (droppedCount(mediaAmbiguousTotal(), ambiguousShown); as more) {
            <li class="admin-import-module__run-errors-more">
              {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
            </li>
          }
        </ul>
      </details>
    }

    @if (runResult.media_partly_linked?.length; as partlyShown) {
      <details class="admin-import-module__run-errors">
        <summary>
          {{ 'TRANS__IMPORT__RUN_MEDIA_PARTLY_LINKED' | translate:
             {count: mediaPartlyLinkedTotal()} }}
        </summary>
        <ul>
          @for (m of runResult.media_partly_linked ?? []; track m.row) {
            <li><span class="bold">{{ m.row }}</span>: {{ m.value }} — {{ m.unlinked.join(', ') }}</li>
          }
          @if (droppedCount(mediaPartlyLinkedTotal(), partlyShown); as more) {
            <li class="admin-import-module__run-errors-more">
              {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
            </li>
          }
        </ul>
      </details>
    }

    @if (runResult.media_shared_file?.length; as sharedShown) {
      <details class="admin-import-module__run-errors">
        <summary>
          {{ 'TRANS__IMPORT__RUN_MEDIA_SHARED_FILE' | translate:
             {count: mediaSharedFileTotal()} }}
        </summary>
        <ul>
          @for (m of runResult.media_shared_file ?? []; track m.row) {
            <li>
              <span class="bold">{{ m.row }}</span>: {{ m.value }} —
              <!-- A row's one file named as its cell is not named again. -->
              @if (m.files.length === 1 && m.files[0] === m.value) {
                {{ 'TRANS__IMPORT__PREFLIGHT_MEDIA_OTHER_ROWS' | translate: {rows: m.rows.join(', ')} }}
              } @else {
                {{ 'TRANS__IMPORT__PREFLIGHT_MEDIA_OVERVIEW_FILE_ROWS' | translate:
                   {file: m.files.join(', '), rows: m.rows.join(', ')} }}
              }
            </li>
          }
          @if (droppedCount(mediaSharedFileTotal(), sharedShown); as more) {
            <li class="admin-import-module__run-errors-more">
              {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
            </li>
          }
        </ul>
      </details>
    }

    @if (runResult.media_already_linked?.length; as alreadyShown) {
      <details class="admin-import-module__run-errors">
        <summary>
          {{ 'TRANS__IMPORT__RUN_MEDIA_ALREADY_LINKED' | translate:
             {count: mediaAlreadyLinkedTotal()} }}
        </summary>
        <ul>
          @for (m of runResult.media_already_linked ?? []; track m.row) {
            <li><span class="bold">{{ m.row }}</span>: {{ m.value }} — {{ m.files.join(', ') }}</li>
          }
          @if (droppedCount(mediaAlreadyLinkedTotal(), alreadyShown); as more) {
            <li class="admin-import-module__run-errors-more">
              {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
            </li>
          }
        </ul>
      </details>
    }
  }

  @if (runResult.orphan_failures.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_ORPHAN_FAILURES' | translate:
           {count: orphanFailuresTotal()} }}
      </summary>
      <ul>
        @for (err of runResult.orphan_failures; track err.row) {
          <li>
            <span class="bold">{{ err.row }}</span> ({{ err.artifact_id }}):
            {{ err.message }}
          </li>
        }
        @if (droppedCount(orphanFailuresTotal(), runResult.orphan_failures.length); as more) {
          <li class="admin-import-module__run-errors-more">
            {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
          </li>
        }
      </ul>
    </details>
  }

  @if (runResult.clean_failures.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_CLEAN_FAILURES' | translate:
           {count: cleanFailuresTotal()} }}
      </summary>
      <ul>
        @for (err of runResult.clean_failures; track err.row) {
          <li><span class="bold">{{ err.row }}</span>: {{ err.message }}</li>
        }
        @if (droppedCount(cleanFailuresTotal(), runResult.clean_failures.length); as more) {
          <li class="admin-import-module__run-errors-more">
            {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
          </li>
        }
      </ul>
    </details>
  }

  @if (runResult.admin_event_failures?.length) {
    <details class="admin-import-module__run-errors">
      <summary>
        {{ 'TRANS__IMPORT__RUN_ADMIN_EVENT_FAILURES' | translate:
           {count: adminEventFailuresTotal()} }}
      </summary>
      <ul>
        @for (err of runResult.admin_event_failures; track err.artifact_id + err.group_key) {
          <li>
            <span class="bold">{{ err.row }}</span> ({{ err.artifact_id }}):
            {{ err.message }}
          </li>
        }
        @if (droppedCount(adminEventFailuresTotal(), runResult.admin_event_failures!.length); as more) {
          <li class="admin-import-module__run-errors-more">
            {{ 'TRANS__IMPORT__RUN_AND_N_MORE' | translate: {count: more} }}
          </li>
        }
      </ul>
    </details>
  }

  @if (adminEventRetryResult; as retryResult) {
    <p class="admin-import-module__admin-event-retry-result">
      {{ 'TRANS__IMPORT__ADMIN_EVENT_RETRY_RESULT' | translate:
         {succeeded: retryResult.succeeded, retried: retryResult.retried} }}
    </p>
  }

  @if (runResult.reindex_hint; as reindexHint) {
    @if (reindexHint.superobject_types.length || reindexHint.meta_types.length) {
      <div class="admin-import-module__reindex-hint">
        <p class="bold">{{ 'TRANS__IMPORT__RUN_CREATED_TITLE' | translate }}</p>
        <ul>
          @for (t of reindexHint.superobject_types; track t.id) {
            <li>{{ t.label | translate }}: {{ t.count }}</li>
          }
          @for (t of reindexHint.meta_types; track t.id) {
            <li>{{ t.label | translate }}: {{ t.count }}</li>
          }
        </ul>
      </div>
    }
  }

  @if (runResult.clean_failures.length || runResult.orphan_failures.length
      || runResult.admin_event_failures?.length) {
    <div class="admin-import-module__run-result-actions">
      @if (runResult.clean_failures.length) {
        <button
          mat-stroked-button
          type="button"
          [disabled]="running"
          (click)="retryFailedRows.emit()"
        >
          {{ 'TRANS__IMPORT__RETRY_FAILED_ROWS' | translate:
             {count: cleanFailuresTotal()} }}
        </button>
      }
      @if (runResult.admin_event_failures?.length) {
        <button
          mat-stroked-button
          type="button"
          [disabled]="running"
          (click)="retryAdminEvents.emit()"
        >
          {{ 'TRANS__IMPORT__RETRY_ADMIN_EVENT_FAILURES' | translate:
             {count: adminEventFailuresTotal()} }}
        </button>
      }
      <button
        mat-stroked-button
        type="button"
        (click)="downloadFailedRowsCsv()"
      >
        {{ 'TRANS__IMPORT__DOWNLOAD_FAILED_ROWS' | translate }}
      </button>
    </div>
  }
</div>
